import type { ReactNode } from 'react'
import type { LinkComponentProps } from '@tanstack/react-router'
import { Link as RouterLink } from '@tanstack/react-router'

import { cn } from '@gto/ui/cn'
import { Link } from '@gto/ui/link'
import { Typography } from '@gto/ui/typography'

const footerLinkClassName =
  'text-text-secondary no-underline transition-colors duration-300 hover:text-primary-main hover:underline hover:decoration-primary-main hover:[text-decoration-thickness:1px] hover:[text-underline-offset:25%] [&_svg]:transition-colors [&_svg]:hover:text-primary-main'

export type FooterLinkExternal = {
  children: ReactNode
  icon?: ReactNode
  href: string
} & Omit<React.ComponentPropsWithoutRef<'a'>, 'children' | 'href'>

export type FooterLinkInternal = {
  children: ReactNode
  icon?: ReactNode
} & Omit<LinkComponentProps, 'children'>

export type FooterLinkProps = FooterLinkExternal | FooterLinkInternal

export const FooterLink = (props: FooterLinkProps) => {
  const { children, icon } = props

  const content = (
    <span className="flex items-center justify-start gap-1 sm:justify-center">
      {icon}
      <Typography variant="body3">{children}</Typography>
    </span>
  )

  if ('to' in props) {
    const { children: _c, icon: _i, className, to, ...routerRest } = props

    return (
      <RouterLink
        className={cn(footerLinkClassName, className)}
        to={to}
        {...routerRest}
      >
        {content}
      </RouterLink>
    )
  }

  const {
    href,
    children: _c,
    icon: _i,
    className,
    component: _unusedComponent,
    gaTag: _unusedGaTag,
    ...anchorRest
  } = props as FooterLinkExternal & { component?: unknown; gaTag?: unknown }

  const isHttp = href.startsWith('http')

  return (
    <Link
      className={cn(footerLinkClassName, className)}
      external={isHttp}
      href={href}
      underline="onHover"
      {...anchorRest}
    >
      {content}
    </Link>
  )
}
